"use client"

import * as React from "react"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { StatusBadge } from "@/components/ui/status-badge"
import { EvaluationTermCardHeader, EvaluationTermViewDetailsButton } from "@/components/evaluation-dashboard/evaluation-term-card-header"
import { OperationsLifecycleSection } from "@/components/evaluation-dashboard/operations/operations-lifecycle-section"
import { OperationsTermTable } from "@/components/evaluation-dashboard/operations/operations-term-table"
import { OperationsTermWorkQueueCompact } from "@/components/evaluation-dashboard/operations/operations-term-work-queue-compact"
import { OperationsTermWorkQueueSheet } from "@/components/evaluation-dashboard/operations/operations-term-work-queue-sheet"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import { EVAL_DASHBOARD_SECTION_H2_CLASS } from "@/lib/evaluation-dashboard-a11y"
import { termTimelineStatusMeta } from "@/lib/evaluation-term-utils"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"
import type { EvaluationWorkQueueRow } from "@/lib/mock/evaluation-work-queue"
import { cn } from "@/lib/utils"

interface OperationsTermSectionProps {
  term: EvaluationTerm
  rows: EvaluationWorkQueueRow[]
  showLifecycle?: boolean
  /** Paired layout: card + table only. */
  compact?: boolean
  /** Side by side with last closed term: compact lifecycle + condensed queue. */
  layout?: "default" | "paired"
}

function currentTermSectionLabel(term: EvaluationTerm): string {
  if (term.timelinePosition === "current") {
    return term.counts.live > 0 ? "Live term" : "Current term"
  }
  if (term.timelinePosition === "upcoming") return "Upcoming term"
  if (term.timelinePosition === "future") return "Future term"
  return "Term"
}

function termOperationsTimelineMeta(term: EvaluationTerm) {
  if (term.timelinePosition === "current" && term.counts.live === 0) {
    return { label: "Current", tone: "info" as const, icon: "fa-calendar" }
  }
  return termTimelineStatusMeta(term.timelinePosition)
}

export function OperationsTermSection({
  term,
  rows,
  showLifecycle = false,
  compact = false,
  layout = "default",
}: OperationsTermSectionProps) {
  const timeline = termOperationsTimelineMeta(term)
  const sectionId = `eval-operations-term-${term.id}`
  const isPaired = layout === "paired"
  const [workQueueSheetOpen, setWorkQueueSheetOpen] = React.useState(false)
  const [workQueueFocusRow, setWorkQueueFocusRow] =
    React.useState<EvaluationWorkQueueRow | null>(null)

  const openWorkQueueSheet = React.useCallback((row: EvaluationWorkQueueRow) => {
    setWorkQueueFocusRow(row)
    setWorkQueueSheetOpen(true)
  }, [])

  const handleWorkQueueSheetOpenChange = React.useCallback((open: boolean) => {
    setWorkQueueSheetOpen(open)
    if (!open) setWorkQueueFocusRow(null)
  }, [])

  return (
    <section
      aria-labelledby={compact ? undefined : `${sectionId}-heading`}
      className={cn("flex flex-col gap-4", isPaired && "h-full")}
    >
      {!compact ? (
        <DashboardSectionTitle
          as="h2"
          id={`${sectionId}-heading`}
          className={EVAL_DASHBOARD_SECTION_H2_CLASS}
        >
          {currentTermSectionLabel(term)}
        </DashboardSectionTitle>
      ) : null}

      <Card size="sm" className={cn("flex min-w-0 flex-col", isPaired && "min-h-0 flex-1")}>
        <EvaluationTermCardHeader
          term={term}
          trailing={
            <StatusBadge
              label={timeline.label}
              tone={timeline.tone}
              size="md"
              icon={timeline.icon}
            />
          }
        />
        <CardContent className={cn("flex flex-col gap-4", isPaired && "flex-1")}>
          {!compact && showLifecycle ? (
            <OperationsLifecycleSection
              term={term}
              density={isPaired ? "compact" : "default"}
            />
          ) : null}
          {isPaired ? (
            <OperationsTermWorkQueueCompact
              rows={rows}
              onOpenWorkQueue={openWorkQueueSheet}
            />
          ) : (
            <OperationsTermTable termId={term.id} rows={rows} />
          )}
        </CardContent>
        <CardFooter
          className={cn(
            "flex flex-wrap gap-2 border-t border-border/60 pt-4",
            isPaired && "mt-auto",
          )}
        >
          <EvaluationTermViewDetailsButton term={term} />
        </CardFooter>
      </Card>

      {isPaired ? (
        <OperationsTermWorkQueueSheet
          term={term}
          summaryRows={rows}
          focusRow={workQueueFocusRow}
          open={workQueueSheetOpen}
          onOpenChange={handleWorkQueueSheetOpenChange}
          onFocusRowChange={setWorkQueueFocusRow}
        />
      ) : null}
    </section>
  )
}
